import {t} from 'i18next';
import React, {
  ReactElement,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from 'react';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import navConstants from '../../../../constants/navConstants';
import {
  setCurrentFieldTemplateId,
  setStrReportId,
} from '../../../../redux/slices/strSlice';
import AurButton from '../../../common/aur-button/AurButton';
import {AurDropdownButton} from '../../../common/aur-button/AurDropdownButton';
import AurIcon, {IconColors, IconNames} from '../../../common/aur-icon/AurIcon';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';
import AurPopup from '../../../common/aur-popup/AurPopup';
import {STR_FLOW} from '../../search-term-report/CreateSTR';
import {STRStatusType} from '../hook/useSTRStatus';
import AurSvgIcon from '../../../common/aur-svg-icon/AurSvgIcon';
import {useAnalytics} from '../../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_FILE_EXTENTIONS,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';

const stringPath = 'eca.searchTermReport.viewSTR.STRButtonBar.';
type STR_Button_Type = 'continue' | 'secondary';

interface Props {
  status: STRStatusType | null;
  onRunAllTerm: (allRun: boolean) => void;
  onRunPendingTerm: () => void;
  onRetryError: () => void;
  onExportReport: () => void;
  onDeleteReport?: () => void;
  reportId: string;
  fieldTemplateId: string;
  failedTermErrorCount: number;
  havePendingToRun: boolean;
  onStop: () => void;
  totalNumberOfTerm: number;
  loadingButtonType?: null | 'all' | 'pending' | 'retryError';
}

const STRButtonBar: React.FC<Props> = ({
  status,
  onRunAllTerm,
  onRunPendingTerm,
  onRetryError,
  onExportReport,
  onDeleteReport,
  reportId,
  fieldTemplateId,
  failedTermErrorCount,
  havePendingToRun,
  onStop,
  totalNumberOfTerm,
  loadingButtonType = null,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {trackGAEvent} = useAnalytics();

  const [showDeleteConfirmModal, setShowDeleteConfirmModal] =
    useState<boolean>(false);
  const [confirmStopAlertShow, setConfirmStopAlertShow] =
    useState<boolean>(false);

  useEffect(() => {
    if (confirmStopAlertShow && status !== STRStatusType.InProgress)
      setConfirmStopAlertShow(false);
  }, [status, confirmStopAlertShow]);

  const handleEditClick = useCallback(() => {
    trackButtonClick(
      t('gaTracking.global.edit'),
      location.host + navConstants.str.baseSTR + `?Flow=${STR_FLOW.EDIT}`
    );
    dispatch(setCurrentFieldTemplateId(parseInt(fieldTemplateId)));
    dispatch(setStrReportId(reportId));
    navigate(navConstants.str.baseSTR + `?Flow=${STR_FLOW.EDIT}`);
  }, [fieldTemplateId, reportId]);

  const trackButtonClick = (name: string, action: string) => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: name,
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.BODY,
        action: action,
      },
    });
  };

  const handleRunAllTermClick = useCallback(() => {
    onRunAllTerm(true);
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.str.runAllTerm'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.BODY,
        action: t('gaTracking.str.runAllTermAction'),
      },
    });
  }, [onRunAllTerm]);

  const handleRunPendingTermsClick = useCallback(() => {
    onRunPendingTerm();
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.str.runPendingTerm'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.BODY,
        action: t('gaTracking.str.runPendingTermAction'),
      },
    });
  }, [onRunPendingTerm]);

  const handleRetryErrorClick = useCallback(() => {
    onRetryError();
    trackButtonClick(
      t('gaTracking.str.retryError'),
      t('gaTracking.str.retryErrorAction')
    );
  }, [onRetryError]);

  const handleExportReportClick = useCallback(() => {
    onExportReport();
    trackGAEvent({
      eventName: GA_EVENT_NAMES.FILE_DOWNLOAD,
      file_extension: GA_FILE_EXTENTIONS.XLSX,
      click: {
        name: t('gaTracking.str.downloadReport'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.BODY,
        action: t('gaTracking.str.downloadReportAction'),
      },
    });
  }, [onExportReport]);

  const handleStopConfirmClick = useCallback(() => {
    setConfirmStopAlertShow(false);
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.str.stopRunning'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action: t('gaTracking.str.stopRunningOkAction'),
      },
      subPage: {
        subpage_name: t('gaTracking.str.stopRunningModal'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
    onStop && onStop();
  }, [onStop]);

  const getButtonItemWithDefault = (
    title: string,
    clickHandler: () => void,
    testId: string,
    startIcon?: ReactElement,
    type: STR_Button_Type = 'continue'
  ) => {
    return {
      name: t(stringPath + title),
      disabled: true,
      onClick: clickHandler,
      dataTestId: testId,
      startIcon: startIcon,
      className: '',
      type: type,
    };
  };

  const buttonBarItem = useMemo(() => {
    const items = [];

    // 1. Run all button item with different state
    const runAllTerm = getButtonItemWithDefault(
      'runAllTerm',
      handleRunAllTermClick,
      'runAllTerms',
      undefined,
      'continue'
    );

    const runPendingTerms = getButtonItemWithDefault(
      'runPendingTerms',
      handleRunPendingTermsClick,
      'runPendingTerms',
      undefined,
      'continue'
    );

    const retryError = getButtonItemWithDefault(
      'retryError',
      handleRetryErrorClick,
      'retryErrors',
      undefined,
      'continue'
    );

    const downloadReportButton = getButtonItemWithDefault(
      'downloadReport',
      handleExportReportClick,
      'downloadReport',
      <AurSvgIcon src="../assests/images/download_icon.svg" />,
      'secondary'
    );

    const editButton = getButtonItemWithDefault(
      'edit',
      handleEditClick,
      'edit',
      undefined,
      'secondary'
    );

    switch (status) {
      case STRStatusType.Ready:
        runAllTerm.disabled = false;
        editButton.disabled = false;
        break;
      case STRStatusType.Failed:
        runAllTerm.disabled = false;
        editButton.disabled = false;
        runPendingTerms.disabled = !havePendingToRun;
        retryError.disabled = failedTermErrorCount === 0;
        break;
      case STRStatusType.InProgress:
        switch (loadingButtonType) {
          case 'retryError':
            retryError.startIcon = <AurLoadingIndicator size={16} />;
            retryError.className = 'retry-error-button';
            break;
          case 'pending':
            runPendingTerms.startIcon = <AurLoadingIndicator size={16} />;
            runPendingTerms.className = 'run-pending-terms-button';
            break;
          default:
            runAllTerm.startIcon = <AurLoadingIndicator size={16} />;
            runAllTerm.className = 'run-all-term-button';
            break;
        }
        break;
      case STRStatusType.Completed:
      case STRStatusType.CompletedWithError:
        runAllTerm.disabled = false;
        editButton.disabled = false;
        downloadReportButton.disabled = false;
        runPendingTerms.disabled = !havePendingToRun;
        retryError.disabled = failedTermErrorCount === 0;
        break;
      case STRStatusType.Stopped:
        runAllTerm.disabled = false;
        editButton.disabled = false;
        runPendingTerms.disabled = !havePendingToRun;
        retryError.disabled = failedTermErrorCount === 0;
        break;
      default:
        break;
    }

    // When total number of terms is zero then disable buttons to execute all type of run term operation
    if (totalNumberOfTerm === 0) {
      runAllTerm.disabled = true;
      runPendingTerms.disabled = true;
      retryError.disabled = true;
    }

    items.push(runAllTerm);
    items.push(runPendingTerms);
    items.push(retryError);
    items.push(downloadReportButton);
    items.push(editButton);
    return items;
  }, [
    status,
    havePendingToRun,
    failedTermErrorCount,
    totalNumberOfTerm,
    loadingButtonType,
    onRunAllTerm,
    onRunPendingTerm,
    onRetryError,
  ]);

  return (
    <div className="view-str-buttonbar-container">
      <div className="buttonbar-left-container">
        {buttonBarItem.slice(0, 3).map((item) => (
          <AurButton
            label={item.name}
            type={item.type as 'continue' | 'secondary'}
            size="Medium"
            disabled={item.disabled}
            dataTestId={item.dataTestId}
            startIcon={item.startIcon}
            onClick={item.onClick}
            classNames={item.className}
            key={item.dataTestId}
          />
        ))}
      </div>
      <div className="buttonbar-right-container">
        {buttonBarItem.slice(3).map((item) => (
          <AurButton
            label={item.name}
            type={item.type as 'continue' | 'secondary'}
            size="Medium"
            disabled={item.disabled}
            dataTestId={item.dataTestId}
            startIcon={item.startIcon}
            onClick={item.onClick}
            classNames={item.className}
            key={item.name}
          />
        ))}
        <div className="aur-responsive-tabset-dropdown">
          <AurDropdownButton
            classNames="aur-responsive-tabset-dropdown-icon"
            type="secondary"
            aria-live="polite"
            size="Medium"
            iconType="Other"
            menuItemHeight="large"
            menuItemWidth="mediumSmall"
            menuItemTextSize="large"
            menuItems={[
              {
                label: t(stringPath + 'menuItem.stopRunning'),
                onClick: () => {
                  setConfirmStopAlertShow(true);
                  trackButtonClick(
                    t('gaTracking.str.stopRunning'),
                    t('gaTracking.str.stopRunningAction')
                  );
                  trackGAEvent({
                    eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
                    subPage: {
                      subpage_name: t('gaTracking.str.stopRunningModal'),
                      subpage_type: GA_SUBPAGE_TYPE.MODAL,
                    },
                  });
                },
                disabled: status !== STRStatusType.InProgress,
              },
              {
                label: t(stringPath + 'menuItem.deleteReport'),
                onClick: () => {
                  setShowDeleteConfirmModal(true);
                  trackGAEvent({
                    eventName: GA_EVENT_NAMES.DELETE,
                    click: {
                      name: t('gaTracking.str.deleteReport'),
                      type: GA_CLICK_TYPES.BUTTON,
                      region: GA_REGIONS.BODY,
                      action: t('gaTracking.str.deleteAction'),
                    },
                  });
                  trackGAEvent({
                    eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
                    subPage: {
                      subpage_name: t('gaTracking.str.deleteStrModal'),
                      subpage_type: GA_SUBPAGE_TYPE.MODAL,
                    },
                  });
                },
                disabled: status === STRStatusType.InProgress,
              },
            ]}
            menuDirectionLeft={false}
            dropdownRole="DropDown"
            elementID="view-report-dropdown-button"
          >
            <AurIcon
              icon={IconNames.More}
              color={IconColors.Custom}
              customClassName="aur-responsive-tabset-dropdown-icon-color"
            />
          </AurDropdownButton>
        </div>
      </div>
      <AurPopup
        onOk={() => {
          setShowDeleteConfirmModal(false);
          onDeleteReport && onDeleteReport();
          trackGAEvent({
            eventName: GA_EVENT_NAMES.DELETE,
            click: {
              name: t('gaTracking.str.deleteReport'),
              type: GA_CLICK_TYPES.BUTTON,
              region: GA_REGIONS.MODAL,
              action: t('gaTracking.str.deleteStrOkAction'),
            },
            subPage: {
              subpage_name: t('gaTracking.str.deleteStrModal'),
              subpage_type: GA_SUBPAGE_TYPE.MODAL,
            },
          });
        }}
        onCancel={() => {
          setShowDeleteConfirmModal(false);
          trackGAEvent({
            eventName: GA_EVENT_NAMES.CLICK,
            click: {
              name: t('gaTracking.global.cancel'),
              type: GA_CLICK_TYPES.BUTTON,
              region: GA_REGIONS.MODAL,
              action: t('gaTracking.str.deleteStrCancelAction'),
            },
            subPage: {
              subpage_name: t('gaTracking.str.deleteStrModal'),
              subpage_type: GA_SUBPAGE_TYPE.MODAL,
            },
          });
        }}
        open={showDeleteConfirmModal}
        showPopupTitle
        popupTitle={t('common.justToConfirm')}
        titleCustomClass="popup-title-label"
        showCloseIcon
        showPopupHeading
        popupHeading={t(
          'eca.searchTermReport.reviewSearch.unSavedChangesModal.popupHeading'
        )}
        contentType="text"
        popupContent={t(
          'eca.searchTermReport.reviewSearch.unSavedChangesModal.popupContent'
        )}
        labelActionCancel={t('buttons.cancel')}
        labelActionOk={t(stringPath + 'deleteReport')}
        className="aur-unsaved-changes-popup delete-report-confirm-popup"
      />
      <AurPopup
        className="aur-unsaved-changes-popup stop-report-processing-popup"
        open={confirmStopAlertShow}
        popupTitle={t('common.justToConfirm')}
        popupHeading={t('eca.searchTermReport.viewSTR.stopConfirmAlert.title')}
        popupContent={t(
          'eca.searchTermReport.viewSTR.stopConfirmAlert.message'
        )}
        popupContentClass="message-content"
        titleCustomClass="popup-title-label"
        labelActionOk={t(
          'eca.searchTermReport.viewSTR.stopConfirmAlert.stopProcessing'
        )}
        labelActionCancel={t('buttons.cancel')}
        showPopupHeading={true}
        showPopupTitle={true}
        showCloseIcon={true}
        onCancel={() => {
          setConfirmStopAlertShow(false);
          trackGAEvent({
            eventName: GA_EVENT_NAMES.CLICK,
            click: {
              name: t('gaTracking.global.cancel'),
              type: GA_CLICK_TYPES.BUTTON,
              region: GA_REGIONS.MODAL,
              action: t('gaTracking.str.stopRunningCancelAction'),
            },
            subPage: {
              subpage_name: t('gaTracking.str.stopRunningModal'),
              subpage_type: GA_SUBPAGE_TYPE.MODAL,
            },
          });
        }}
        onOk={handleStopConfirmClick}
      />
    </div>
  );
};

export default STRButtonBar;
